import { DatabaseType, StorageType, CloudProvider, Runtime } from '@/interfaces/entities/entity_blueprints/MetaDataInterface';

export const SETTINGS_CONFIG = {
  sections: [
    {
      id: "general",
      name: "General",
      icon: "Settings",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "Environment Setup",
        },
        {
          id: "runtime",
          type: "dropdown",
          label: "Runtime Environment",
          path: ["runtime"],
          options: Object.values(Runtime),
          gridCol: "sm:col-span-1",
        }
      ],
    },
    {
      id: "auth",
      name: "Authentication",
      icon: "Lock",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "User Authentication",
        },
        {
          id: "authentication",
          type: "toggle",
          label: "Enable User Authentication",
          path: ["authentication"],
        },
        {
          id: "authMethods",
          type: "conditionalGroup",
          dependsOn: "authentication",
          groupLabel: "Providers",
          fields: [
            {
              id: "emailPassword",
              type: "toggle",
              label: "Email/Password (Standard)",
              path: ["authenticationMethods", "emailPassword"],
            },
            {
              id: "googleAuth",
              type: "toggle",
              label: "Google SSO",
              path: ["authenticationMethods", "googleAuth"],
            },
          ],
        },
      ],
    },
    {
      id: "storage",
      name: "Storage",
      icon: "Database",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "Data and File Storage",
        },
        {
          id: "primaryDatabase",
          type: "dropdown",
          label: "Primary Database Type",
          path: ["databaseType"],
          options: Object.values(DatabaseType),
          gridCol: "sm:col-span-1",
        },
        {
          id: "fileStorageToggle",
          type: "toggle",
          label: "Enable File Storage",
          path: ["fileStorage"],
        },
        {
          id: "fileStorageBucketGroup",
          type: "conditionalGroup",
          label: "File Storage Bucket",
          gridCol: "sm:col-span-1",
          dependsOn: "fileStorageToggle",
          fields: [
            {
              id: "fileStorageBucket",
              type: "dropdown",
              label: "File Storage Bucket",
              path: ["storageType"],
              options: Object.values(StorageType),
              gridCol: "sm:col-span-1",
            },
          ]
        },
      ],
    },
    {
      id: "rateLimit",
      name: "Rate Limiting",
      icon: "Zap",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "API Rate Limiting",
        },
        {
          id: "rateLimiterEnabled",
          type: "toggle",
          label: "Enable Global Rate Limiter",
          path: ["rateLimiterEnabled"],
        },
        {
          id: "rateLimiterConfig",
          type: "conditionalGroup",
          dependsOn: "rateLimiterEnabled",
          fields: [
            {
              id: "windowMs",
              type: "input",
              inputType: "number",
              label: "Window (ms)",
              path: ["rateLimiterConfig", "windowMs"],
              defaultValue: 900000,
              gridCol: "sm:col-span-1",
            },
            {
              id: "maxRequests",
              type: "input",
              inputType: "number",
              label: "Max Requests per Window",
              path: ["rateLimiterConfig", "maxRequests"],
              defaultValue: 100,
              gridCol: "sm:col-span-1",
            },
            {
              id: "standardHeaders",
              type: "toggle",
              label: "Standard Headers (X-RateLimit-*)",
              path: ["rateLimiterConfig", "standardHeaders"],
            },
            {
              id: "legacyHeaders",
              type: "toggle",
              label: "Legacy Headers (X-Draft-*)",
              path: ["rateLimiterConfig", "legacyHeaders"],
            },
            {
              id: "skipFailedRequests",
              type: "toggle",
              label: "Skip Failed Requests",
              path: ["rateLimiterConfig", "skipFailedRequests"],
            },
            {
              id: "message",
              type: "input",
              inputType: "text",
              label: "Custom Block Message",
              path: ["rateLimiterConfig", "message"],
              defaultValue: "Too many requests from this IP, please try again later",
            },
          ],
        },
      ],
    },
    {
      id: "cors",
      name: "CORS",
      icon: "Globe",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "Cross-Origin Resource Sharing (CORS)",
        },
        {
          id: "corsEnabled",
          type: "toggle",
          label: "Enable CORS Policy",
          path: ["corsEnabled"],
        },
        {
          id: "corsConfig",
          type: "conditionalGroup",
          dependsOn: "corsEnabled",
          fields: [
            {
              id: "allowedOrigins",
              type: "input",
              inputType: "text",
              label: "Allowed Origins (comma separated)",
              path: ["corsConfig", "allowedOrigins"],
              isArray: true,
            },
            {
              id: "allowedHeaders",
              type: "input",
              inputType: "text",
              label: "Allowed Headers (comma separated)",
              path: ["corsConfig", "allowedHeaders"],
              isArray: true,
            },
            {
              id: "allowedMethods",
              type: "methodToggle",
              label: "Allowed Methods",
              path: ["corsConfig", "allowedMethods"],
              options: ["GET", "POST", "PUT", "DELETE", "PATCH", "OPTIONS"],
            },
            {
              id: "allowCredentials",
              type: "toggle",
              label: "Allow Credentials",
              path: ["corsConfig", "allowCredentials"],
            },
          ],
        },
      ],
    },
    {
      id: "autoscaling",
      name: "Cloud & Autoscaling",
      icon: "Zap",
      fields: [
        {
          id: "sectionTitle",
          type: "sectionTitle",
          label: "Cloud Autoscaling Configuration",
        },
        {
          id: "autoscalingEnabled",
          type: "toggle",
          label: "Enable Autoscaling",
          path: ["autoScaleConfig", "enabled"],
        },
        {
          id: "autoscalingConfig",
          type: "conditionalGroup",
          dependsOn: "autoScaleConfig.enabled",
          fields: [
            {
              id: "cloudProvider",
              type: "dropdown",
              label: "Cloud Provider",
              path: ["autoScaleConfig", "cloudProvider"],
              options: Object.values(CloudProvider),
              gridCol: "sm:col-span-1",
            },
            {
              id: "scalingPolicy",
              type: "dropdown",
              label: "Scaling Policy",
              path: ["autoScaleConfig", "scalingPolicy"],
              options: ["horizontal", "vertical", "hybrid"],
              gridCol: "sm:col-span-1",
            },
            {
              id: "minInstances",
              type: "input",
              inputType: "number",
              label: "Min Instances",
              path: ["autoScaleConfig", "minInstances"],
              defaultValue: 1,
              gridCol: "sm:col-span-1",
            },
            {
              id: "maxInstances",
              type: "input",
              inputType: "number",
              label: "Max Instances",
              path: ["autoScaleConfig", "maxInstances"],
              defaultValue: 10,
              gridCol: "sm:col-span-1",
            },
            {
              id: "targetCPU",
              type: "input",
              inputType: "number",
              label: "Target CPU Utilization (%)",
              path: ["autoScaleConfig", "targetCPUUtilization"],
              defaultValue: 70,
              gridCol: "sm:col-span-1",
            },
            {
              id: "targetMemory",
              type: "input",
              inputType: "number",
              label: "Target Memory Utilization (%)",
              path: ["autoScaleConfig", "targetMemoryUtilization"],
              defaultValue: 80,
              gridCol: "sm:col-span-1",
            },
            {
              id: "cooldownPeriod",
              type: "input",
              inputType: "number",
              label: "Cooldown Period (seconds)",
              path: ["autoScaleConfig", "cooldownPeriod"],
              defaultValue: 300,
              gridCol: "sm:col-span-1",
            },
            // AWS specific fields - only show when AWS is selected
            {
              id: "awsRegion",
              type: "input",
              inputType: "text",
              label: "AWS Region",
              path: ["autoScaleConfig", "awsRegion"],
              defaultValue: "us-east-1",
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AWS,
              },
            },
            {
              id: "awsAutoScalingGroupName",
              type: "input",
              inputType: "text",
              label: "AWS Auto Scaling Group Name",
              path: ["autoScaleConfig", "awsAutoScalingGroupName"],
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AWS,
              },
            },
            {
              id: "awsLoadBalancerArn",
              type: "input",
              inputType: "text",
              label: "AWS Load Balancer ARN",
              path: ["autoScaleConfig", "awsLoadBalancerArn"],
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AWS,
              },
            },
            // GCP specific fields - only show when GCP is selected
            {
              id: "gcpProjectId",
              type: "input",
              inputType: "text",
              label: "GCP Project ID",
              path: ["autoScaleConfig", "gcpProjectId"],
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.GCP,
              },
            },
            {
              id: "gcpZone",
              type: "input",
              inputType: "text",
              label: "GCP Zone",
              path: ["autoScaleConfig", "gcpZone"],
              defaultValue: "us-central1-a",
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.GCP,
              },
            },
            {
              id: "gcpServiceName",
              type: "input",
              inputType: "text",
              label: "GCP Service Name",
              path: ["autoScaleConfig", "gcpServiceName"],
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.GCP,
              },
            },
            // Azure specific fields - only show when Azure is selected
            {
              id: "azureResourceGroup",
              type: "input",
              inputType: "text",
              label: "Azure Resource Group",
              path: ["autoScaleConfig", "azureResourceGroup"],
              defaultValue: "myResourceGroup",
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AZURE,
              },
            },
            {
              id: "azureLocation",
              type: "input",
              inputType: "text",
              label: "Azure Location",
              path: ["autoScaleConfig", "azureLocation"],
              defaultValue: "East US",
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AZURE,
              },
            },
            {
              id: "azureAppServiceName",
              type: "input",
              inputType: "text",
              label: "Azure App Service Name",
              path: ["autoScaleConfig", "azureAppServiceName"],
              gridCol: "sm:col-span-1",
              showWhen: {
                path: ["autoScaleConfig", "cloudProvider"],
                value: CloudProvider.AZURE,
              },
            },
          ],
        },
      ],
    },
    {
      id: "rbac",
      name: "Access Control",
      icon: "Shield",
      fields: [
        {
          id: "rbacSectionTitle",
          type: "sectionTitle",
          label: "Role-Based Access Control (RBAC)",
        },
        {
          id: "rbacEnabled",
          type: "toggle",
          label: "Enable RBAC",
          path: ["rbac", "enabled"],
        },
        {
          id: "rbacConfig",
          type: "conditionalGroup",
          dependsOn: "rbac.enabled",
          groupLabel: "RBAC Settings",
          fields: [
            {
              id: "rbacCanModify",
              type: "toggle",
              label: "Allow runtime role modification",
              path: ["rbac", "canModify"],
            },
          ],
        },
      ],
    },
  ],
};
